import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Play } from "lucide-react";
import type { MemoryWithChildren } from "@/hooks/useMemories";
import { supabase } from "@/lib/supabase";
import { TRANSFORMS } from "@/lib/imageUrl";
import { useChildren } from "@/hooks/useChildren";
import MemoryDetailSheet from "@/components/MemoryDetailSheet";
import MemoryPhotoGrid from "@/components/MemoryPhotoGrid";
import FullScreenImage from "@/components/FullScreenImage";
import { getMemoryDisplayTitle } from "@/lib/memoryTitle";
import {
  CARD_COLORS,
  bodyStyle,
  cardShellStyle,
  fullCtaStyle,
  labelStyle,
  titleStyle,
} from "./types";

interface FeedMemoryCardProps {
  memory: MemoryWithChildren;
}

function audioStoragePath(raw: string): string | null {
  if (!raw.startsWith("http")) return raw.split("?")[0];
  const idx = raw.indexOf("/audio-memories/");
  return idx !== -1 ? raw.slice(idx + "/audio-memories/".length).split("?")[0] : null;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

const FeedMemoryCard = ({ memory }: FeedMemoryCardProps) => {
  const { t } = useTranslation();
  const colors = CARD_COLORS.memory;
  const [detailOpen, setDetailOpen] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const { children: allChildren } = useChildren();

  const childCreatorName = memory.user_id
    ? (allChildren.find((c) => c.promoted_to != null && c.promoted_to === memory.user_id)?.name ?? null)
    : null;

  const isShortie = memory.memory_type === "shortie";
  const mt = memory.memory_type as string;
  const isVoiceArtifact = mt === "voice_moment" || mt === "voice" || mt === "time_capsule";
  const [audioSignedUrl, setAudioSignedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isShortie || !memory.thumbnail_url) return;
    supabase.storage
      .from("memory-images")
      .createSignedUrl(memory.thumbnail_url, 3600, { transform: TRANSFORMS.feedCard })
      .then(({ data }) => {
        if (data?.signedUrl) setThumbUrl(data.signedUrl);
      });
  }, [isShortie, memory.thumbnail_url]);

  useEffect(() => {
    if (!isVoiceArtifact || !memory.audio_url) return;
    const path = audioStoragePath(memory.audio_url);
    if (!path) return;
    supabase.storage
      .from("audio-memories")
      .createSignedUrl(path, 3600)
      .then(({ data }) => {
        if (data?.signedUrl) setAudioSignedUrl(data.signedUrl);
      });
  }, [isVoiceArtifact, memory.audio_url]);

  const childNames = memory.memory_participants
    .filter((mp) => mp.child_id !== null)
    .map((mp) => mp.children?.name)
    .filter((n): n is string => !!n);

  const excerpt = memory.ai_chapter || memory.raw_transcript || "";
  const truncated =
    excerpt.length > 140 ? excerpt.slice(0, 137) + "…" : excerpt;

  const title =
    getMemoryDisplayTitle(memory) ||
    (isShortie ? t("cards.memory.shortieTitle") : t("memory.fallbackTitle"));

  return (
    <>
      <div style={cardShellStyle(colors.bg, colors.border)}>
        {/* Label + memory title — always above any artefact */}
        <div style={{ marginBottom: 8 }}>
          <div style={labelStyle(colors.body)}>
            {isShortie ? t("cards.memory.labelShortie") : t("cards.memory.label")}
          </div>
          <p
            style={{
              ...titleStyle(colors.title),
              marginTop: 4,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {title}
          </p>
        </div>

        {/* Shortie thumbnail */}
        {isShortie && thumbUrl && (
          <div
            style={{ position: "relative", marginBottom: 8, borderRadius: 10, overflow: "hidden" }}
            onClick={() => setDetailOpen(true)}
          >
            <img src={thumbUrl} alt="" style={{ width: "100%", display: "block", maxHeight: 120, objectFit: "cover" }} />
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Play size={16} color="white" style={{ marginLeft: 2 }} />
              </div>
            </div>
            {memory.video_duration_seconds != null && (
              <span style={{ position: "absolute", bottom: 4, right: 6, fontSize: 10, fontFamily: "monospace", color: "#fff", backgroundColor: "rgba(0,0,0,0.6)", padding: "1px 5px", borderRadius: 3 }}>
                {`${Math.floor(memory.video_duration_seconds / 60)}:${String(Math.floor(memory.video_duration_seconds % 60)).padStart(2, "0")}`}
              </span>
            )}
          </div>
        )}

        {/* Photo grid (renders nothing if memory has no photos) */}
        {!isShortie && memory.image_url && (
          <div style={{ marginBottom: 8 }}>
            <MemoryPhotoGrid
              memoryId={memory.id}
              fallbackImageUrl={memory.image_url}
              size="sm"
              onPhotoClick={(url) => setLightboxUrl(url)}
              onOverflowClick={() => setDetailOpen(true)}
            />
          </div>
        )}

        {/* Inline audio player for voice memories */}
        {isVoiceArtifact && memory.audio_url && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ marginBottom: 8 }}
          >
            <audio
              controls
              preload="metadata"
              src={audioSignedUrl ?? undefined}
              style={{ display: "block", width: "100%", height: 32 }}
            />
          </div>
        )}

        {/* Body text + metadata — flex: 1 fills remaining space */}
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <p
            style={{
              ...bodyStyle(colors.body),
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              flex: 1,
            }}
          >
            {truncated}
          </p>

          {memory.ai_chapter && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                marginTop: 6,
                fontSize: 10,
                fontWeight: 500,
                color: colors.body,
                opacity: 0.7,
                alignSelf: "flex-start",
              }}
            >
              🌰 {t("mori.keptBy")}
            </span>
          )}

          {memory.ai_status === "failed" && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginTop: 6,
                fontSize: 10,
                fontStyle: "italic",
                color: colors.body,
                opacity: 0.65,
                alignSelf: "flex-start",
              }}
            >
              {t("feed.memoryCard.storyFailedHint")}
            </span>
          )}

          {childCreatorName && (
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              marginTop: 6,
              backgroundColor: "#fff1f2",
              border: "1px solid #fecdd3",
              borderRadius: 20,
              padding: "2px 8px",
              fontSize: 9,
              fontWeight: 600,
              color: "#be123c",
            }}>
              {t("memory.fromChild", { name: childCreatorName })}
            </span>
          )}

          <p
            style={{
              ...bodyStyle(colors.body),
              fontSize: 11,
              opacity: 0.75,
              marginTop: 4,
            }}
          >
            {[childNames.join(", "), formatDate(memory.created_at)]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>

        {/* Bottom: CTA */}
        <div style={{ paddingTop: 12 }}>
          <button
            type="button"
            onClick={() => setDetailOpen(true)}
            style={fullCtaStyle(colors.title, colors.bg)}
          >
            {t("cards.memory.cta")}
          </button>
        </div>
      </div>

      <MemoryDetailSheet
        memoryId={memory.id}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
      />

      <FullScreenImage
        src={lightboxUrl ?? ""}
        isOpen={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
      />
    </>
  );
};

export default FeedMemoryCard;
